import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../api/axios";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import CreditCardForm from "./CreditCardForm";
import axios from "axios";
import heic2any from "heic2any";

const stripePromise = loadStripe("pk_live_51RhCPIIr9wzO6YRcodYxMIPM7k8c5Rp5nK3R1xVAH2UqRSfgXJaufqKZlPShTAa6cK9ZZfQyi760HQmlo2vpujKw00XFC38CqU");

const compressImage = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const MAX_WIDTH = 1200;
                const MAX_HEIGHT = 1200;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > MAX_WIDTH) {
                        height = height * (MAX_WIDTH / width);
                        width = MAX_WIDTH;
                    }
                } else {
                    if (height > MAX_HEIGHT) {
                        width = width * (MAX_HEIGHT / height);
                        height = MAX_HEIGHT;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    const compressedFile = new File([blob], file.name, {
                        type: 'image/jpeg',
                        lastModified: Date.now()
                    });
                    resolve(compressedFile);
                }, 'image/jpeg', 0.7); // 0.7 quality
            };
            img.onerror = (error) => {
                alert("❌ Error loading image");
                reject(error);
            };
        };
        reader.onerror = (error) => {
            alert("❌ Error reading file");
            reject(error);
        };
    });
};

export default function VerifyIDNow() {
    const [error, setError] = useState("");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const { id } = useParams();
    const navigate = useNavigate();
    //const handleFileChange = (e) => setFile(e.target.files[0]);

    const handleFileChange = async (e) => {
        const selected = e.target.files[0];
        if (!selected) {
            setError("No file selected.");
            alert("❌ No file selected");
            return;
        }

        // Show file details in alert
        alert(`📸 Selected: ${selected.name}\nSize: ${(selected.size / (1024 * 1024)).toFixed(2)}MB`);

        // Check if it's an image
        if (!selected.type.startsWith('image/')) {
            setError("Please select an image file.");
            alert("❌ Please select an image file only");
            return;
        }

        // Handle large files
        if (selected.size > 10 * 1024 * 1024) { // 10MB limit
            setError("File is too large. Please select a smaller image.");
            alert("❌ File is too large (max 10MB). Please select a smaller image.");
            return;
        }

        try {
            alert("⏳ Processing image...");
            // Create a compressed version of the image
            const compressedFile = await compressImage(selected);
            setFile(compressedFile);
            alert("✅ Image processed successfully!");
        } catch (err) {
            console.error("Error processing image:", err);
            setError("Error processing image. Please try again.");
            alert("❌ Error processing image. Please try again.");
        }
    };

    const handleIDSubmit = async () => {
        if (!file) {
            setError("Please upload your ID proof.");
            alert("❌ Please upload your ID proof first");
            return;
        }

        setUploading(true);
        alert("📤 Starting upload...");
        
        const formData = new FormData();
        formData.append("id", id);
        formData.append("image", file, `photo_${Date.now()}.jpg`);

        // n8n webhook URL
        const WEBHOOK_URL = "https://whuk-64169.whukhost.com/webhook/verify-age";

        try {
        // Direct POST request without preflight OPTIONS
        const res = await axios({
            method: 'post',
            url: WEBHOOK_URL,
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            // Skip OPTIONS preflight
            validateStatus: status => status >= 200 && status < 300,
            maxBodyLength: Infinity,
            maxContentLength: Infinity
        });

        if (res.data.check) {
            alert("✅ Verification successful!");
            navigate("/paymentverified/" + id);
        } else {
            setError("Access denied. Date of birth failed verification requirements");
            alert("❌ Access denied. Age verification failed.");
        }
    } catch (err) {
        if (err.message === "Network Error") {
            alert("❌ Upload failed. Please try again.");
        } else {
            const errorMessage = err.response?.data?.message || err.message;
            alert(`❌ Upload Error: ${errorMessage}`);
        }
        
        setError("Verification failed. Please try again.");
    } finally {
        setUploading(false);
    }
};

    // Add visual feedback for the upload state in your JSX
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-[#F7EBFC] px-4">
            <img src="https://verify.18verified.com/logo.png" alt="Logo" className="max-w-[200px] w-full mb-6" />
            <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-[#E3BCF4]">
                <h2 className="text-2xl text-center mb-6 text-[#652F9B]">
                    Success! Your subscription is now live. Please choose a verification method:
                </h2>

                {/* Option 1: Credit Card */}
                <div className="mb-6 text-center">
                    <h3 className="font-semibold mb-2">Verify via Credit Card</h3>

                    <Elements stripe={stripePromise}>
                        <CreditCardForm id={id} setError={setError} options={{ hidePostalCode: true }} />
                    </Elements>
                </div>

                <div className="w-full max-w-md">
                    <h2 className="text-2xl font-bold text-center mb-6 text-[#652F9B]">
                        OR
                    </h2>
                </div>
                <hr className="my-6 border-[#ccc]" />

                {/* Option 2: ID Proof Upload */}
                <div className="text-center">
                    <h2 className="text-xl font-bold text-[#652F9B] text-center">Verify using photo ID</h2>
                    <br></br>
                    {/* Custom file-picker */}
                    <label className="inline-flex cursor-pointer items-center justify-center rounded-xl bg-[#EFE7F9] px-6 py-2 text-sm font-semibold text-[#652F9B] shadow hover:bg-[#e0d3f5] focus:outline-none focus:ring-2 focus:ring-[#8E29CD]">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="mr-2 h-5 w-5"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3 16.5V8.25A2.25 2.25 0 015.25 6h1.086a2.25 2.25 0 001.59-.659l1.428-1.428a2.25 2.25 0 011.591-.658h4.345A2.25 2.25 0 0117.5 6h1.25A2.25 2.25 0 0121 8.25v8.25A2.25 2.25 0 0118.75 18.75H5.25A2.25 2.25 0 013 16.5z"
                            />
                        </svg>
                        Choose Photo ID
                        <input
                            type="file"
                            accept="image/png, image/jpeg, image/heic"
                            onChange={handleFileChange}
                            className="absolute h-0 w-0 overflow-hidden opacity-0"
                        />
                        {/* ✅ Green Tick */}
                        {file && (
                            <span className="text-green-600 text-xl font-bold">✔️</span>
                        )}
                    </label><br></br><br></br>

                    <button
                        onClick={handleIDSubmit}
                        className="bg-[#8E29CD] hover:bg-[#701eaa] text-white py-2 px-6 rounded-xl"
                        disabled={uploading}
                    >
                        {uploading ? "⏳ Uploading..." : "Verify now"}
                    </button>
                </div>

                {error && <p className="mt-4 text-red-600 text-sm">{error}</p>}
            </div>
        </div>
    );
}